import { useEffect, useState } from "react";
import AccountButton from "../AccountButton";
import AccountInput from "../AccountInput";
import AccountSection from "../AccountSection";
import { getInsurance, getProfile, updateProfile } from "../api/apiFunctions";
import { t } from "../i18n";
import { useSubscriptionAccess } from "../context/SubscriptionAccessContext";

export default function InsurancePage({ lang = "en", goToTab }) {
  const [form, setForm] = useState({
    primaryInsurance: [],
    secondaryInsurance: [],
    lifeInsurance: [],
    disabilityInsurance: [],
    primaryInsuranceState: "",
    secondaryInsuranceState: "",
    lifeInsuranceState: "",
    disabilityInsuranceState: "",
    primaryInsuranceMemberId: "",
    secondaryInsuranceMemberId: "",
    lifeInsuranceMemberId: "",
    disabilityInsuranceMemberId: "",
    lifeInsuranceAmount: "",
    disabilityInsuranceAmount: "",
    policy: "",
  });

  const { dontHaveAccess } = useSubscriptionAccess();

  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Insurance states
  const [selectedPrimaryInsurance, setSelectedPrimaryInsurance] = useState("");
  const [selectedSecondaryInsurance, setSelectedSecondaryInsurance] =
    useState("");
  const [selectedLifeInsurance, setSelectedLifeInsurance] = useState("");
  const [selectedDisabilityInsurance, setSelectedDisabilityInsurance] =
    useState("");
  const [otherPrimaryInsurance, setOtherPrimaryInsurance] = useState("");
  const [otherSecondaryInsurance, setOtherSecondaryInsurance] = useState("");
  const [otherLifeInsurance, setOtherLifeInsurance] = useState("");
  const [otherDisabilityInsurance, setOtherDisabilityInsurance] = useState("");

  const secondaryInsuranceProviders = [
    { value: "BCBS", label: "Blue Cross Blue Shield" },
    { value: "UnitedHealthcare", label: "UnitedHealthcare" },
    { value: "Elevance", label: "Elevance Health / Anthem" },
    { value: "Centene", label: "Centene Corporation" },
    { value: "Humana", label: "Humana Inc." },
    { value: "CVS", label: "CVS Health / Aetna" },
    { value: "Kaiser", label: "Kaiser Permanente" },
    { value: "Cigna", label: "Cigna Group" },
    { value: "HCSC", label: "Health Care Service Corporation (HCSC)" },
    { value: "Molina", label: "Molina Healthcare Inc." },
    { value: "GuideWell", label: "GuideWell / Florida Blue" },
    { value: "Independence", label: "Independence Health Group" },
    { value: "Highmark", label: "Highmark Health" },
    { value: "UPMC", label: "UPMC Health Plan" },
    {
      value: "Point32Health",
      label: "Point32Health (Harvard Pilgrim + Tufts)",
    },
    { value: "Priority", label: "Priority Health" },
    { value: "MVP", label: "MVP Health Care" },
    { value: "CDPHP", label: "CDPHP" },
    { value: "Healthfirst", label: "Healthfirst" },
    { value: "MetroPlus", label: "MetroPlusHealth" },
    { value: "Geisinger", label: "Geisinger Health Plan" },
    { value: "Medica", label: "Medica Inc." },
    { value: "Sanford", label: "Sanford Health Plan" },
    { value: "Quartz", label: "Quartz Health Solutions" },
    { value: "Dean", label: "Dean Health Plan" },
    { value: "Oscar", label: "Oscar Health" },
    { value: "Bright", label: "Bright Health" },
    { value: "Wellpoint", label: "Wellpoint / Elevance" },
    { value: "Amerigroup", label: "Amerigroup Corporation" },
    { value: "CareSource", label: "CareSource" },
    { value: "HealthNet", label: "Health Net Incorporated" },
    { value: "Aetna", label: "Aetna Inc." },
    { value: "Beacon", label: "Beacon Health Options" },
    {
      value: "MedicaidAdvantage",
      label: "Medicaid Advantage Specialist Plans",
    },
    {
      value: "MedicareAdvantage",
      label: "Medicare Advantage Specialist Plans",
    },
    { value: "Centivo", label: "Centivo" },
    { value: "Alignment", label: "Alignment Healthcare" },
    { value: "Aloha", label: "Aloha Care" },
    { value: "AmeriHealth", label: "AmeriHealth Carriers" },
    { value: "other", label: t("other", lang) || "Other" },
  ];

  // Insurance provider options
  const insuranceProviders = [
    { value: "", label: t("selectInsurance", lang) || "Select Insurance" },
    { value: "MedicareA", label: "Medicare Part A – Hospital" },
    { value: "MedicareB", label: "Medicare Part B – Medical Insurance" },
    { value: "MedicareC", label: "Medicare Part C – Medicare Advantage" },
    {
      value: "MedicareD",
      label: "Medicare Part D – Additional Coverage OR Secondary Coverage",
    },

    {
      value: "MedicareA_B",
      label: "Medicare Part A – Hospital + Medicare Part B – Medical Insurance",
    },
    {
      value: "MedicareA_C",
      label:
        "Medicare Part A – Hospital + Medicare Part C – Medicare Advantage",
    },
    {
      value: "MedicareA_D",
      label:
        "Medicare Part A – Hospital + Medicare Part D – Additional Coverage OR Secondary Coverage",
    },
    {
      value: "MedicareB_C",
      label:
        "Medicare Part B – Medical Insurance + Medicare Part C – Medicare Advantage",
    },
    {
      value: "MedicareB_D",
      label:
        "Medicare Part B – Medical Insurance + Medicare Part D – Additional Coverage OR Secondary Coverage",
    },
    {
      value: "MedicareC_D",
      label:
        "Medicare Part C – Medicare Advantage + Medicare Part D – Additional Coverage OR Secondary Coverage",
    },

    {
      value: "MedicareA_B_C",
      label:
        "Medicare Part A – Hospital + Medicare Part B – Medical Insurance + Medicare Part C – Medicare Advantage",
    },
    {
      value: "MedicareA_B_D",
      label:
        "Medicare Part A – Hospital + Medicare Part B – Medical Insurance + Medicare Part D – Additional Coverage OR Secondary Coverage",
    },
    {
      value: "MedicareA_C_D",
      label:
        "Medicare Part A – Hospital + Medicare Part C – Medicare Advantage + Medicare Part D – Additional Coverage OR Secondary Coverage",
    },
    {
      value: "MedicareB_C_D",
      label:
        "Medicare Part B – Medical Insurance + Medicare Part C – Medicare Advantage + Medicare Part D – Additional Coverage OR Secondary Coverage",
    },
    {
      value: "MedicareA_B_C_D",
      label:
        "Medicare Part A – Hospital + Medicare Part B – Medical Insurance + Medicare Part C – Medicare Advantage + Medicare Part D – Additional Coverage OR Secondary Coverage",
    },
    { value: "BCBS", label: "Blue Cross Blue Shield" },
    { value: "UnitedHealthcare", label: "UnitedHealthcare" },
    { value: "Elevance", label: "Elevance Health / Anthem" },
    { value: "Centene", label: "Centene Corporation" },
    { value: "Humana", label: "Humana Inc." },
    { value: "CVS", label: "CVS Health / Aetna" },
    { value: "Kaiser", label: "Kaiser Permanente" },
    { value: "Cigna", label: "Cigna Group" },
    { value: "HCSC", label: "Health Care Service Corporation (HCSC)" },
    { value: "Molina", label: "Molina Healthcare Inc." },
    { value: "GuideWell", label: "GuideWell / Florida Blue" },
    { value: "Independence", label: "Independence Health Group" },
    { value: "Highmark", label: "Highmark Health" },
    { value: "UPMC", label: "UPMC Health Plan" },
    {
      value: "Point32Health",
      label: "Point32Health (Harvard Pilgrim + Tufts)",
    },
    { value: "Priority", label: "Priority Health" },
    { value: "MVP", label: "MVP Health Care" },
    { value: "CDPHP", label: "CDPHP" },
    { value: "Healthfirst", label: "Healthfirst" },
    { value: "MetroPlus", label: "MetroPlusHealth" },
    { value: "Geisinger", label: "Geisinger Health Plan" },
    { value: "Medica", label: "Medica Inc." },
    { value: "Sanford", label: "Sanford Health Plan" },
    { value: "Quartz", label: "Quartz Health Solutions" },
    { value: "Dean", label: "Dean Health Plan" },
    { value: "Oscar", label: "Oscar Health" },
    { value: "Bright", label: "Bright Health" },
    { value: "Wellpoint", label: "Wellpoint / Elevance" },
    { value: "Amerigroup", label: "Amerigroup Corporation" },
    { value: "CareSource", label: "CareSource" },
    { value: "HealthNet", label: "Health Net Incorporated" },
    { value: "Aetna", label: "Aetna Inc." },
    { value: "Beacon", label: "Beacon Health Options" },
    {
      value: "MedicaidAdvantage",
      label: "Medicaid Advantage Specialist Plans",
    },
    {
      value: "MedicareAdvantage",
      label: "Medicare Advantage Specialist Plans",
    },
    { value: "Centivo", label: "Centivo" },
    { value: "Alignment", label: "Alignment Healthcare" },
    { value: "Aloha", label: "Aloha Care" },
    { value: "AmeriHealth", label: "AmeriHealth Carriers" },
    { value: "other", label: t("other", lang) || "Other" },
  ];

  const lifeInsuranceProviders = [
    { value: "", label: "Select Life Insurance Provider" },

    {
      value: "NorthwesternMutual",
      label: "Northwestern Mutual Life Insurance Company",
    },
    { value: "NewYorkLife", label: "New York Life Insurance Company" },
    {
      value: "MassMutual",
      label: "Massachusetts Mutual Life Insurance Company",
    },
    { value: "MetLife", label: "Metropolitan Life Insurance Company" },
    { value: "Prudential", label: "Prudential Insurance Company of America" },
    {
      value: "LincolnNational",
      label: "Lincoln National Life Insurance Company",
    },
    { value: "ProtectiveLife", label: "Protective Life Insurance Company" },
    {
      value: "TIAA",
      label: "Teachers Insurance and Annuity Association of America (TIAA)",
    },
    { value: "Nationwide", label: "Nationwide Life Insurance Company" },
    { value: "Corebridge", label: "Corebridge Life Insurance Company" },
    { value: "Athene", label: "Athene Annuity and Life Company" },
    {
      value: "WesternSouthern",
      label: "The Western and Southern Life Insurance Company",
    },
    { value: "MutualOfOmaha", label: "Mutual of Omaha Insurance Company" },
    { value: "PacificLife", label: "Pacific Life Insurance Company" },
    {
      value: "AmericanGeneral",
      label: "American General Life Insurance Company",
    },
    {
      value: "JohnHancock",
      label: "John Hancock Life Insurance Company (U.S.A.)",
    },
    {
      value: "JacksonNational",
      label: "Jackson National Life Insurance Company",
    },
    { value: "Ameritas", label: "Ameritas Life Insurance Corp." },
    { value: "Principal", label: "Principal Life Insurance Company" },
    { value: "Unum", label: "Unum Life Insurance Company of America" },
    {
      value: "MidlandNational",
      label: "Midland National Life Insurance Company",
    },
    {
      value: "NorthAmerican",
      label: "North American Company for Life and Health Insurance",
    },
    { value: "Symetra", label: "Symetra Life Insurance Company" },
    { value: "BannerLife", label: "Banner Life Insurance Company" },
    { value: "PennMutual", label: "The Penn Mutual Life Insurance Company" },
    {
      value: "AllianzLife",
      label: "Allianz Life Insurance Company of North America",
    },
    { value: "Transamerica", label: "Transamerica Life Insurance Company" },
    { value: "MinnesotaLife", label: "Minnesota Life Insurance Company" },
    {
      value: "GuardianLife",
      label: "The Guardian Life Insurance Company of America",
    },
    { value: "Equitable", label: "Equitable Financial Life Insurance Company" },
    { value: "LSW", label: "Life Insurance Company of the Southwest" },
    { value: "Equitrust", label: "Equitrust Life Insurance Company" },
    {
      value: "AmericanUnited",
      label: "American United Life Insurance Company",
    },
    { value: "StandardInsurance", label: "Standard Insurance Company" },
    {
      value: "AmericanEquity",
      label: "American Equity Investment Life Insurance Company",
    },
    { value: "StateFarm", label: "State Farm Life Insurance Company" },
    { value: "USAA", label: "USAA Life Insurance Company" },
    { value: "Brighthouse", label: "Brighthouse Life Insurance Company" },
    { value: "Genworth", label: "Genworth Life Insurance Company" },
    {
      value: "Foresters",
      label: "Foresters Life Insurance and Annuity Company",
    },
    { value: "GlobeLife", label: "Globe Life and Accident Insurance Company" },
    {
      value: "ColonialLife",
      label: "Colonial Life & Accident Insurance Company",
    },
    {
      value: "LibertyNational",
      label: "Liberty National Life Insurance Company",
    },
    { value: "NationalLife", label: "National Life Insurance Company" },
    { value: "CincinnatiLife", label: "Cincinnati Life Insurance Company" },
    {
      value: "FidelityGuaranty",
      label: "Fidelity & Guaranty Life Insurance Company",
    },
    { value: "AssurityLife", label: "Assurity Life Insurance Company" },
    {
      value: "IllinoisMutual",
      label: "Illinois Mutual Life Insurance Company",
    },
    { value: "GerberLife", label: "Gerber Life Insurance Company" },
    { value: "TransWorld", label: "Trans World Assurance Company" },

    { value: "other", label: "Other" },
  ];

  const disabilityInsuranceProviders = [
    { value: "", label: "Select Disability Insurance Provider" },

    {
      value: "GuardianLife",
      label: "Guardian Life Insurance Company of America",
    },
    { value: "PrincipalLife", label: "Principal Life Insurance Company" },
    { value: "MutualOfOmaha", label: "Mutual of Omaha Insurance Company" },
    { value: "StandardInsurance", label: "The Standard Insurance Company" },
    {
      value: "MassMutual",
      label: "Massachusetts Mutual Life Insurance Company (MassMutual)",
    },
    {
      value: "NorthwesternMutual",
      label: "Northwestern Mutual Life Insurance Company",
    },
    { value: "UnumLife", label: "Unum Life Insurance Company of America" },
    {
      value: "LincolnNational",
      label: "Lincoln National Life Insurance Company",
    },
    { value: "NewYorkLife", label: "New York Life Insurance Company" },
    { value: "MetLifeUSA", label: "MetLife Insurance Company USA" },
    {
      value: "RelianceStandard",
      label: "Reliance Standard Life Insurance Company",
    },
    { value: "AssurityLife", label: "Assurity Life Insurance Company" },
    {
      value: "IllinoisMutual",
      label: "Illinois Mutual Life Insurance Company",
    },
    { value: "Ameritas", label: "Ameritas Life Insurance Corp." },
    { value: "OhioNational", label: "Ohio National Life Insurance Company" },
    { value: "SymetraLife", label: "Symetra Life Insurance Company" },
    { value: "PacificLife", label: "Pacific Life Insurance Company" },
    {
      value: "EquitableLife",
      label: "Equitable Financial Life Insurance Company",
    },
    {
      value: "ColonialLife",
      label: "Colonial Life & Accident Insurance Company",
    },
    { value: "CignaLife", label: "Cigna Life Insurance Company of America" },
    { value: "Aflac", label: "Aflac Insurance Company" },
    {
      value: "HartfordLife",
      label: "Hartford Life and Accident Insurance Company",
    },
    {
      value: "SunLifeUS",
      label: "Sun Life Assurance Company of Canada (U.S.)",
    },
    { value: "NationwideLife", label: "Nationwide Life Insurance Company" },
    { value: "SecurianLife", label: "Securian Life Insurance Company" },
    { value: "KansasCityLife", label: "Kansas City Life Insurance Company" },
    { value: "StateFarmLife", label: "State Farm Life Insurance Company" },
    { value: "USAA", label: "USAA Life Insurance Company" },
    { value: "Voya", label: "Voya Retirement Insurance and Annuity Company" },
    { value: "ProtectiveLife", label: "Protective Life Insurance Company" },
    { value: "ManhattanLife", label: "Manhattan Life Insurance Company" },
    { value: "NationalLife", label: "National Life Insurance Company" },
    { value: "CincinnatiLife", label: "Cincinnati Life Insurance Company" },
    { value: "AmericanFidelity", label: "American Fidelity Assurance Company" },
    { value: "Trustmark", label: "Trustmark Insurance Company" },
    {
      value: "MetLifeConnecticut",
      label: "MetLife Life and Annuity Company of Connecticut",
    },
    {
      value: "StandardIndiana",
      label: "Standard Life Insurance Company of Indiana",
    },
    { value: "LINA", label: "Life Insurance Company of North America" },
    {
      value: "LibertyLifeBoston",
      label: "Liberty Life Assurance Company of Boston",
    },
    {
      value: "GreatAmericanLife",
      label: "Great American Life Insurance Company",
    },

    { value: "other", label: "Other" },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch insurance data on component mount
  useEffect(() => {
    const fetchInsuranceData = async () => {
      try {
        setLoading(true);
        setErrors({});

        // Get token from localStorage
        const token =
          localStorage.getItem("authToken") ||
          sessionStorage.getItem("authToken");

        if (!token) {
          throw new Error(
            "Authentication token not found. Please login again.",
          );
        }

        // Fetch insurance data from API
        const response = await getInsurance(token);

        if (response.success && response.data) {
          const data = response.data;

          // Set form data
          setForm({
            primaryInsurance: data.primaryInsurance
              ? [data.primaryInsurance]
              : [],
            secondaryInsurance: data.secondaryInsurance
              ? [data.secondaryInsurance]
              : [],
            lifeInsurance: data.lifeInsurance ? [data.lifeInsurance] : [],
            disabilityInsurance: data.disabilityInsurance
              ? [data.disabilityInsurance]
              : [],
            primaryInsuranceState: data.primaryInsuranceState || "",
            secondaryInsuranceState: data.secondaryInsuranceState || "",
            lifeInsuranceState: data.lifeInsuranceState || "",
            disabilityInsuranceState: data.disabilityInsuranceState || "",
            primaryInsuranceMemberId: data.primaryInsuranceMemberId || "",
            secondaryInsuranceMemberId: data.secondaryInsuranceMemberId || "",
            lifeInsuranceMemberId: data.lifeInsuranceMemberId || "",
            disabilityInsuranceMemberId: data.disabilityInsuranceMemberId || "",
            lifeInsuranceAmount: data.lifeInsuranceAmount || "",
            disabilityInsuranceAmount: data.disabilityInsuranceAmount || "",
            policy: data.policy || "",
          });

          // Set selected insurance values based on current data
          if (data.primaryInsurance) {
            const primaryOption = insuranceProviders.find(
              (opt) => opt.label === data.primaryInsurance,
            );
            if (primaryOption) {
              setSelectedPrimaryInsurance(primaryOption.value);
            } else {
              setSelectedPrimaryInsurance("other");
              setOtherPrimaryInsurance(data.primaryInsurance);
            }
          }

          if (data.secondaryInsurance) {
            const secondaryOption = secondaryInsuranceProviders.find(
              (opt) => opt.label === data.secondaryInsurance,
            );
            if (secondaryOption) {
              setSelectedSecondaryInsurance(secondaryOption.value);
            } else {
              setSelectedSecondaryInsurance("other");
              setOtherSecondaryInsurance(data.secondaryInsurance);
            }
          }

          if (data.lifeInsurance) {
            const lifeOption = lifeInsuranceProviders.find(
              (opt) => opt.label === data.lifeInsurance,
            );
            if (lifeOption) {
              setSelectedLifeInsurance(lifeOption.value);
            } else {
              setSelectedLifeInsurance("other");
              setOtherLifeInsurance(data.lifeInsurance);
            }
          }

          if (data.disabilityInsurance) {
            const disabilityOption = disabilityInsuranceProviders.find(
              (opt) => opt.label === data.disabilityInsurance,
            );
            if (disabilityOption) {
              setSelectedDisabilityInsurance(disabilityOption.value);
            } else {
              setSelectedDisabilityInsurance("other");
              setOtherDisabilityInsurance(data.disabilityInsurance);
            }
          }
        } else {
          throw new Error("Failed to fetch insurance data");
        }
      } catch (error) {
        console.error("Error fetching insurance data:", error);
        setErrors({
          general:
            error.message || "Failed to load insurance data. Please try again.",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchInsuranceData();
  }, []);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setErrors({});
      setSuccess(null);

      // Get token from localStorage
      const token =
        localStorage.getItem("authToken") ||
        sessionStorage.getItem("authToken");

      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      // Fetch current profile to merge with insurance data
      const currentProfile = await getProfile(token);

      if (!currentProfile.success || !currentProfile.data) {
        throw new Error("Failed to fetch current profile data");
      }

      // Prepare complete profile data with insurance updates
      const profileData = {
        // Keep existing profile data
        dateOfBirth: currentProfile.data.dateOfBirth || "",
        height: currentProfile.data.height || 0,
        weight: currentProfile.data.weight || 0,
        gender: currentProfile.data.gender || "",
        bloodType: currentProfile.data.bloodType || "",
        nationality: currentProfile.data.nationality || "",
        countryOfBirth: currentProfile.data.countryOfBirth || "",
        ethnicity: currentProfile.data.ethnicity || "not_specified",
        primaryPhoneNumber: currentProfile.data.primaryPhoneNumber || "",
        secondaryPhoneNumber: currentProfile.data.secondaryPhoneNumber || "",
        secondaryMobileNumber: currentProfile.data.secondaryMobileNumber || "",
        // Update insurance fields
        primaryInsurance: form.primaryInsurance[0] || "",
        primaryInsuranceMemberId: form.primaryInsuranceMemberId || "",
        secondaryInsurance: form.secondaryInsurance[0] || "",
        secondaryInsuranceMemberId: form.secondaryInsuranceMemberId || "",
        lifeInsurance: form.lifeInsurance[0] || "",
        lifeInsuranceMemberId: form.lifeInsuranceMemberId || "",
        lifeInsuranceAmount:
          form.lifeInsuranceAmount !== ""
            ? parseFloat(form.lifeInsuranceAmount) || 0
            : 0,
        disabilityInsurance: form.disabilityInsurance[0] || "",
        disabilityInsuranceMemberId: form.disabilityInsuranceMemberId || "",
        disabilityInsuranceAmount:
          form.disabilityInsuranceAmount !== ""
            ? parseFloat(form.disabilityInsuranceAmount) || 0
            : 0,
        primaryInsuranceState: form.primaryInsuranceState || "",
        secondaryInsuranceState: form.secondaryInsuranceState || "",
        lifeInsuranceState: form.lifeInsuranceState || "",
        disabilityInsuranceState: form.disabilityInsuranceState || "",
        policy: form.policy,
        // Keep existing emergency contacts
        emergencyContactName: currentProfile.data.emergencyContactName || "",
        emergencyContactPhone: currentProfile.data.emergencyContactPhone || "",
        emergencyRelation: currentProfile.data.emergencyRelation || "",
        emergencyContacts: currentProfile.data.emergencyContacts || [],
        allowReleaseMedicalInfo:
          currentProfile.data.allowReleaseMedicalInfo || false,
        medicalInfoReleaseIndividuals:
          currentProfile.data.medicalInfoReleaseIndividuals || [],
      };

      // Update profile with insurance information
      const response = await updateProfile(profileData, token);

      if (!response.success) {
        // Handle specific insurance update errors
        const insuranceErrors = {};
        const message =
          response.message || "Failed to update insurance information";

        if (message.toLowerCase().includes("primary")) {
          insuranceErrors.primaryInsurance = message;
        } else if (message.toLowerCase().includes("secondary")) {
          insuranceErrors.secondaryInsurance = message;
        } else if (message.toLowerCase().includes("life")) {
          insuranceErrors.lifeInsurance = message;
        } else if (message.toLowerCase().includes("disability")) {
          insuranceErrors.disabilityInsurance = message;
        } else {
          // Default to general error
          insuranceErrors.general = message;
        }

        setErrors(insuranceErrors);
        return false; // Return false to indicate failure
      }

      // Show success message
      setSuccess("Insurance information updated successfully!");
      return true; // Return true to indicate success
    } catch (error) {
      setErrors({
        general:
          error.message ||
          "Failed to save insurance information. Please try again.",
      });
      return false; // Return false to indicate failure
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));

    // Clear success message when user starts typing
    if (success) {
      setSuccess(null);
    }
    // Clear field-specific error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // Insurance handlers
  const handlePrimaryInsuranceChange = (e) => {
    const value = e.target.value;
    setSelectedPrimaryInsurance(value);
    if (value === "other") {
      setForm((prev) => ({
        ...prev,
        primaryInsurance: [],
      }));
    } else if (value) {
      const selectedOption = insuranceProviders.find(
        (option) => option.value === value,
      );
      setForm((prev) => ({
        ...prev,
        primaryInsurance: [selectedOption.label],
      }));
      setOtherPrimaryInsurance("");
    }
    // Reset state field if not BCBS
    if (value !== "BCBS") {
      setForm((prev) => ({ ...prev, primaryInsuranceState: "" }));
    }
  };

  const handleSecondaryInsuranceChange = (e) => {
    const value = e.target.value;
    setSelectedSecondaryInsurance(value);
    if (value === "other") {
      setForm((prev) => ({
        ...prev,
        secondaryInsurance: [],
      }));
    } else if (value) {
      const selectedOption = secondaryInsuranceProviders.find(
        (option) => option.value === value,
      );
      setForm((prev) => ({
        ...prev,
        secondaryInsurance: [selectedOption.label],
      }));
      setOtherSecondaryInsurance("");
    }
    if (value !== "BCBS") {
      setForm((prev) => ({ ...prev, secondaryInsuranceState: "" }));
    }
  };

  const handleLifeInsuranceChange = (e) => {
    const value = e.target.value;
    setSelectedLifeInsurance(value);
    if (value === "other") {
      setForm((prev) => ({
        ...prev,
        lifeInsurance: [],
      }));
    } else if (value) {
      const selectedOption = lifeInsuranceProviders.find(
        (option) => option.value === value,
      );
      setForm((prev) => ({
        ...prev,
        lifeInsurance: [selectedOption.label],
      }));
      setOtherLifeInsurance("");
    }
    if (value !== "BCBS") {
      setForm((prev) => ({ ...prev, lifeInsuranceState: "" }));
    }
  };

  const handleOtherPrimaryInsurance = (e) => {
    const value = e.target.value;
    setOtherPrimaryInsurance(value);
    setForm((prev) => ({
      ...prev,
      primaryInsurance: value.trim() ? [value] : [],
    }));
  };

  const handleOtherSecondaryInsurance = (e) => {
    const value = e.target.value;
    setOtherSecondaryInsurance(value);
    setForm((prev) => ({
      ...prev,
      secondaryInsurance: value.trim() ? [value] : [],
    }));
  };

  const handleOtherLifeInsurance = (e) => {
    const value = e.target.value;
    setOtherLifeInsurance(value);
    setForm((prev) => ({
      ...prev,
      lifeInsurance: value.trim() ? [value] : [],
    }));
  };

  const handleDisabilityInsuranceChange = (e) => {
    const value = e.target.value;
    setSelectedDisabilityInsurance(value);
    if (value === "other") {
      setForm((prev) => ({
        ...prev,
        disabilityInsurance: [],
      }));
    } else if (value) {
      const selectedOption = disabilityInsuranceProviders.find(
        (option) => option.value === value,
      );
      setForm((prev) => ({
        ...prev,
        disabilityInsurance: [selectedOption.label],
      }));
      setOtherDisabilityInsurance("");
    }
    if (value !== "BCBS") {
      setForm((prev) => ({ ...prev, disabilityInsuranceState: "" }));
    }
  };

  const handleOtherDisabilityInsurance = (e) => {
    const value = e.target.value;
    setOtherDisabilityInsurance(value);
    setForm((prev) => ({
      ...prev,
      disabilityInsurance: value.trim() ? [value] : [],
    }));
  };

  // Show loading state
  if (loading) {
    return (
      <div className="h-screen bg-[#f7f8fa] flex flex-col items-center justify-center pb-8">
        <div className="w-full max-w-4xl">
          <div className="bg-white rounded-xl shadow p-8 mt-4">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4962d8]"></div>
              <span className="ml-4 text-lg text-[#2d348f]">
                Loading insurance data...
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f8fa] flex flex-col items-center pb-8">
      <div className="w-full max-w-4xl p-4 md:p-8">
        <div className="bg-white rounded-xl shadow p-8 mt-4">
          <AccountSection>{t("insurance", lang) || "Insurance"}</AccountSection>
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="text-green-600 text-sm">✅ {success}</div>
            </div>
          )}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="text-red-600 text-sm">⚠️ {errors.general}</div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Primary Insurance Section */}
            <div className="flex flex-col mb-4 flex-1 min-w-[180px]">
              <label className="text-xs font-semibold text-[#837f90] mb-1">
                {t("primaryInsurance", lang) || "Primary Insurance"}
              </label>
              <select
                value={selectedPrimaryInsurance}
                disabled={dontHaveAccess}
                onChange={handlePrimaryInsuranceChange}
                className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium mb-2"
              >
                {insuranceProviders.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {selectedPrimaryInsurance === "other" && (
                <input
                  type="text"
                  value={otherPrimaryInsurance}
                  onChange={handleOtherPrimaryInsurance}
                  placeholder={
                    t("enterOtherInsurance", lang) || "Enter other insurance..."
                  }
                  className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium mt-2"
                />
              )}
              {form.primaryInsurance.length > 0 && (
                <div className="space-y-1 mt-2">
                  {form.primaryInsurance.map((insurance, index) => (
                    <div
                      key={index}
                      className="bg-[#f7f8fa] p-2 rounded text-sm"
                    >
                      <span>{insurance}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* State - only editable for BCBS */}
              <div className="mt-4">
                <label className="text-xs font-semibold text-[#837f90] mb-1">
                  {t("insuranceState", lang) || "State"}
                </label>
                <input
                  type="text"
                  value={form.primaryInsuranceState || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      primaryInsuranceState: e.target.value,
                    }))
                  }
                  placeholder={t("enterState", lang) || "Enter state..."}
                  disabled={
                    selectedPrimaryInsurance !== "BCBS" || dontHaveAccess
                  }
                  className={`w-full border-0 border-b-2 ${
                    selectedPrimaryInsurance === "BCBS"
                      ? "border-[#4962d8] focus:border-[#4962d8]"
                      : "border-gray-300"
                  } focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium ${
                    selectedPrimaryInsurance !== "BCBS"
                      ? "opacity-60 cursor-not-allowed"
                      : ""
                  }`}
                />
              </div>
              <div className="mt-4">
                <label className="text-xs font-semibold text-[#837f90] mb-1">
                  {t("primaryInsuranceMemberId", lang) ||
                    "Primary Insurance Member ID"}
                </label>
                <input
                  type="text"
                  value={form.primaryInsuranceMemberId || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      primaryInsuranceMemberId: e.target.value,
                    }))
                  }
                  disabled={dontHaveAccess}
                  placeholder={
                    t("enterPrimaryMemberId", lang) ||
                    "Enter primary insurance member ID..."
                  }
                  className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
                />
              </div>
              {errors.primaryInsurance && (
                <div className="text-red-500 text-sm mt-1">
                  ⚠️ {errors.primaryInsurance}
                </div>
              )}
            </div>

            {/* Secondary Insurance Section */}
            <div className="flex flex-col mb-4 flex-1 min-w-[180px]">
              <label className="text-xs font-semibold text-[#837f90] mb-1">
                {t("secondaryInsurance", lang) || "Secondary Insurance"}
              </label>
              <select
                disabled={dontHaveAccess}
                value={selectedSecondaryInsurance}
                onChange={handleSecondaryInsuranceChange}
                className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium mb-2"
              >
                {secondaryInsuranceProviders.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {selectedSecondaryInsurance === "other" && (
                <input
                  type="text"
                  value={otherSecondaryInsurance}
                  onChange={handleOtherSecondaryInsurance}
                  placeholder={
                    t("enterOtherInsurance", lang) || "Enter other insurance..."
                  }
                  className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium mt-2"
                />
              )}
              {form.secondaryInsurance.length > 0 && (
                <div className="space-y-1 mt-2">
                  {form.secondaryInsurance.map((insurance, index) => (
                    <div
                      key={index}
                      className="bg-[#f7f8fa] p-2 rounded text-sm"
                    >
                      <span>{insurance}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* State - only editable for BCBS */}
              <div className="mt-4">
                <label className="text-xs font-semibold text-[#837f90] mb-1">
                  {t("insuranceState", lang) || "State"}
                </label>
                <input
                  type="text"
                  value={form.secondaryInsuranceState || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      secondaryInsuranceState: e.target.value,
                    }))
                  }
                  placeholder={t("enterState", lang) || "Enter state..."}
                  disabled={
                    selectedSecondaryInsurance !== "BCBS" || dontHaveAccess
                  }
                  className={`w-full border-0 border-b-2 ${
                    selectedSecondaryInsurance === "BCBS"
                      ? "border-[#4962d8] focus:border-[#4962d8]"
                      : "border-gray-300"
                  } focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium ${
                    selectedSecondaryInsurance !== "BCBS"
                      ? "opacity-60 cursor-not-allowed"
                      : ""
                  }`}
                />
              </div>
              <div className="mt-4">
                <label className="text-xs font-semibold text-[#837f90] mb-1">
                  {t("secondaryInsuranceMemberId", lang) ||
                    "Secondary Insurance Member ID"}
                </label>
                <input
                  type="text"
                  value={form.secondaryInsuranceMemberId || ""}
                  disabled={dontHaveAccess}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      secondaryInsuranceMemberId: e.target.value,
                    }))
                  }
                  placeholder={
                    t("enterSecondaryMemberId", lang) ||
                    "Enter secondary insurance member ID..."
                  }
                  className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
                />
              </div>
              {errors.secondaryInsurance && (
                <div className="text-red-500 text-sm mt-1">
                  ⚠️ {errors.secondaryInsurance}
                </div>
              )}
            </div>

            {/* Life Insurance Section */}
            <div className="flex flex-col mb-4 flex-1 min-w-[180px]">
              <label className="text-xs font-semibold text-[#837f90] mb-1">
                {t("lifeInsurance", lang) || "Life Insurance"}
              </label>
              <select
                value={selectedLifeInsurance}
                disabled={dontHaveAccess}
                onChange={handleLifeInsuranceChange}
                className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium mb-2"
              >
                {lifeInsuranceProviders.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {selectedLifeInsurance === "other" && (
                <input
                  type="text"
                  value={otherLifeInsurance}
                  onChange={handleOtherLifeInsurance}
                  placeholder={
                    t("enterOtherInsurance", lang) || "Enter other insurance..."
                  }
                  className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium mt-2"
                />
              )}
              {form.lifeInsurance.length > 0 && (
                <div className="space-y-1 mt-2">
                  {form.lifeInsurance.map((insurance, index) => (
                    <div
                      key={index}
                      className="bg-[#f7f8fa] p-2 rounded text-sm"
                    >
                      <span>{insurance}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* State - only editable for BCBS */}
              <div className="mt-4">
                <label className="text-xs font-semibold text-[#837f90] mb-1">
                  {t("insuranceState", lang) || "State"}
                </label>
                <input
                  type="text"
                  value={form.lifeInsuranceState || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      lifeInsuranceState: e.target.value,
                    }))
                  }
                  placeholder={t("enterState", lang) || "Enter state..."}
                  disabled={selectedLifeInsurance !== "BCBS" || dontHaveAccess}
                  className={`w-full border-0 border-b-2 ${
                    selectedLifeInsurance === "BCBS"
                      ? "border-[#4962d8] focus:border-[#4962d8]"
                      : "border-gray-300"
                  } focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium ${
                    selectedLifeInsurance !== "BCBS"
                      ? "opacity-60 cursor-not-allowed"
                      : ""
                  }`}
                />
              </div>
              <div className="mt-4">
                <label className="text-xs font-semibold text-[#837f90] mb-1">
                  {t("lifeInsuranceMemberId", lang) ||
                    "Life Insurance Member ID"}
                </label>
                <input
                  disabled={dontHaveAccess}
                  type="text"
                  value={form.lifeInsuranceMemberId || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      lifeInsuranceMemberId: e.target.value,
                    }))
                  }
                  placeholder={
                    t("enterLifeMemberId", lang) ||
                    "Enter life insurance member ID..."
                  }
                  className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
                />
              </div>
              <div className="mt-4">
                <label className="text-xs font-semibold text-[#837f90] mb-1">
                  {t("lifeInsuranceAmount", lang) || "Life Insurance Amount"}
                </label>
                <input
                  disabled={dontHaveAccess}
                  type="text"
                  value={form.lifeInsuranceAmount || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      lifeInsuranceAmount: e.target.value,
                    }))
                  }
                  placeholder={
                    t("enterLifeAmount", lang) ||
                    "Enter life insurance amount..."
                  }
                  className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
                />
              </div>
              {errors.lifeInsurance && (
                <div className="text-red-500 text-sm mt-1">
                  ⚠️ {errors.lifeInsurance}
                </div>
              )}
            </div>

            {/* Disability Insurance Section */}
            <div className="flex flex-col mb-4 flex-1 min-w-[180px]">
              <label className="text-xs font-semibold text-[#837f90] mb-1">
                {t("disabilityInsurance", lang) || "Disability Insurance"}
              </label>
              <select
                value={selectedDisabilityInsurance}
                disabled={dontHaveAccess}
                onChange={handleDisabilityInsuranceChange}
                className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium mb-2"
              >
                {disabilityInsuranceProviders.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {selectedDisabilityInsurance === "other" && (
                <input
                  type="text"
                  value={otherDisabilityInsurance}
                  onChange={handleOtherDisabilityInsurance}
                  placeholder={
                    t("enterOtherInsurance", lang) || "Enter other insurance..."
                  }
                  className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium mt-2"
                />
              )}
              {form.disabilityInsurance.length > 0 && (
                <div className="space-y-1 mt-2">
                  {form.disabilityInsurance.map((insurance, index) => (
                    <div
                      key={index}
                      className="bg-[#f7f8fa] p-2 rounded text-sm"
                    >
                      <span>{insurance}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* State - only editable for BCBS */}
              <div className="mt-4">
                <label className="text-xs font-semibold text-[#837f90] mb-1">
                  {t("insuranceState", lang) || "State"}
                </label>
                <input
                  type="text"
                  value={form.disabilityInsuranceState || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      disabilityInsuranceState: e.target.value,
                    }))
                  }
                  placeholder={t("enterState", lang) || "Enter state..."}
                  disabled={
                    selectedDisabilityInsurance !== "BCBS" || dontHaveAccess
                  }
                  className={`w-full border-0 border-b-2 ${
                    selectedDisabilityInsurance === "BCBS"
                      ? "border-[#4962d8] focus:border-[#4962d8]"
                      : "border-gray-300"
                  } focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium ${
                    selectedDisabilityInsurance !== "BCBS"
                      ? "opacity-60 cursor-not-allowed"
                      : ""
                  }`}
                />
              </div>
              <div className="mt-4">
                <label className="text-xs font-semibold text-[#837f90] mb-1">
                  {t("disabilityInsuranceMemberId", lang) ||
                    "Disability Insurance Member ID"}
                </label>
                <input
                  type="text"
                  disabled={dontHaveAccess}
                  value={form.disabilityInsuranceMemberId || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      disabilityInsuranceMemberId: e.target.value,
                    }))
                  }
                  placeholder={
                    t("enterDisabilityMemberId", lang) ||
                    "Enter disability insurance member ID..."
                  }
                  className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
                />
              </div>
              <div className="mt-4">
                <label className="text-xs font-semibold text-[#837f90] mb-1">
                  {t("disabilityInsuranceAmount", lang) ||
                    "Disability Insurance Amount"}
                </label>
                <input
                  type="text"
                  disabled={dontHaveAccess}
                  value={form.disabilityInsuranceAmount || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      disabilityInsuranceAmount: e.target.value,
                    }))
                  }
                  placeholder={
                    t("enterDisabilityAmount", lang) ||
                    "Enter disability insurance amount..."
                  }
                  className="w-full border-0 border-b-2 border-[#4962d8] focus:border-[#4962d8] focus:outline-none focus:shadow-none text-sm py-1 px-0 bg-transparent text-black font-medium"
                />
              </div>
              {errors.disabilityInsurance && (
                <div className="text-red-500 text-sm mt-1">
                  ⚠️ {errors.disabilityInsurance}
                </div>
              )}
            </div>

            {/* Policy Field */}
            <div className="md:col-span-2">
              <AccountInput
                label={t("policy", lang) || "Policy"}
                name="policy"
                value={form.policy}
                onChange={handleChange}
                disabled={dontHaveAccess}
              />
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            {/* <AccountButton
              className="md:w-1/3"
              onClick={() => goToTab("prev")}
              disabled={isSaving}
            >
              <span className="mr-2">&laquo;</span> Back
            </AccountButton> */}
            <AccountButton
              className="md:w-1/3"
              onClick={handleSave}
              disabled={isSaving || dontHaveAccess}
            >
              {isSaving ? "Saving..." : "Save"}
            </AccountButton>
            <AccountButton
              className="md:w-1/3"
              onClick={() => goToTab("next")}
              disabled={isSaving}
            >
              <span className="ml-2">Next &raquo;</span>
            </AccountButton>
          </div>
        </div>
      </div>
    </div>
  );
}
